/**
 * "A declarar — sin importe a pagar": the state of an IVA obligation whose
 * period nets to zero or to a saldo a favor.
 *
 * Reported 2026-09-17 by an accountant on a client carrying ~177.708 a favor:
 * Fiscalo showed the July IVA as VENCIDA and nagged both of them, for money
 * that was never owed. The backend already knew — the rule just lived only in
 * the reminder cron.
 *
 * The declaración is STILL DUE in this state; only the payment is nil. So the
 * obligation stays visible and keeps its due date — we drop the red "Vencida"
 * framing, not the row.
 *
 * NULL / undefined `amountOwedUyu` means NOT COMPUTED YET, never "nothing to
 * pay": an unrefreshed row must keep reading exactly as it did before.
 *
 * Mirrored in apps/mobile/app/agenda.tsx — keep the two in step (portal/mobile
 * alignment rule).
 */

function toNumber(value: string | number | null | undefined): number | null {
  if (value == null) return null;
  if (typeof value === "string" && value.trim() === "") {
    // Number("") and Number("   ") are both 0, which would make a blank
    // amount look like an explicitly computed zero and wrongly downgrade the
    // row to "sin importe a pagar". Blank is unknown, same as null.
    return null;
  }
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

export interface DeclareOnlyInput {
  taxType: string;
  /** Present so a filed obligation keeps reading as "Completado". */
  status?: string;
  amountOwedUyu?: string | number | null;
  saldoAFavorUyu?: string | number | null;
}

/**
 * True when this obligation carries an explicitly computed amount of zero OR
 * BELOW — i.e. there is nothing to pay, only to declare. A negative is treated
 * the same as zero on purpose: whatever produced it, the taxpayer still owes
 * nothing, and "sin importe a pagar" is the honest reading. IVA only: it is
 * the sole tax whose credit/debit position we compute per period.
 */
export function isDeclareOnly(obligation: DeclareOnlyInput): boolean {
  if (obligation.taxType !== "IVA") return false;
  // Already filed wins: a completed obligation whose amount was refreshed to
  // 0 while it was still open must keep saying "Completado", not regress to
  // "a declarar".
  if (obligation.status === "completed") return false;
  const owed = toNumber(obligation.amountOwedUyu);
  return owed !== null && owed <= 0;
}

/** The saldo a favor to show beside it, or 0 when there is none to show. */
export function saldoAFavorOf(obligation: DeclareOnlyInput): number {
  const saldo = toNumber(obligation.saldoAFavorUyu);
  return saldo !== null && saldo > 0 ? saldo : 0;
}
